import { Directive, EmbeddedViewRef, Inject, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { SETTINGS_SERVICE } from '../constants';
import { MenuItemKey, PlatformFeaturesKey, ProfileUserDTO, SectionStatus, SettingsModel, UserRole } from '../models';
import { BaseSettingsGetService, ProfileService } from '../services';

@Directive({
    selector: '[displayMenuItem]',
    standalone: false
})
export class DisplayMenuItemDirective implements OnInit {
  @Input() public displayMenuItem: MenuItemKey;
  @Input() public displayMenuItemAdminPlatform = false;

  private viewRef: EmbeddedViewRef<Object> = null;

  private menuItemFeatureMap = {
    [MenuItemKey.ACHIEVEMENTS]: [PlatformFeaturesKey.ACHIEVEMENTS, PlatformFeaturesKey.CERTIFICATES],
    [MenuItemKey.LIBRARY]: [PlatformFeaturesKey.LIBRARY],
    [MenuItemKey.CALENDAR]: [PlatformFeaturesKey.CALENDAR],
    [MenuItemKey.LEARNING_PATH]: [PlatformFeaturesKey.LEARNING_PATH],
    [MenuItemKey.EVENTS]: [PlatformFeaturesKey.WEBINARS, PlatformFeaturesKey.IN_PERSON_EVENTS],
    [MenuItemKey.COMMUNITY]: [PlatformFeaturesKey.COMMUNITY],
    [MenuItemKey.COUPONS]: [PlatformFeaturesKey.COUPONS],
    [MenuItemKey.GLOSSARY]: [PlatformFeaturesKey.GLOSSARY],
    [MenuItemKey.SITEMAP]: [PlatformFeaturesKey.SITEMAP],
    [MenuItemKey.LEADERBOARD]: [PlatformFeaturesKey.LEADERBOARD],
    [MenuItemKey.CHAT]: [PlatformFeaturesKey.CHAT],
    [MenuItemKey.CERTIFICATES]: [PlatformFeaturesKey.CERTIFICATES],
    [MenuItemKey.MANAGER_VIEW]: [PlatformFeaturesKey.MANAGER_VIEW],
    [MenuItemKey.CHALLENGES]: [PlatformFeaturesKey.CHALLENGES],
    [MenuItemKey.NOTIFICATIONS]: [PlatformFeaturesKey.NOTIFICATIONS],
    [MenuItemKey.WEBINARS]: [PlatformFeaturesKey.WEBINARS],
    [MenuItemKey.IN_PERSON_EVENTS]: [PlatformFeaturesKey.IN_PERSON_EVENTS],
    [MenuItemKey.AI_CHAT]: [PlatformFeaturesKey.AI_CHATBOT]
  };

  constructor(
    private readonly profileService: ProfileService,
    private readonly viewContainer: ViewContainerRef,
    @Inject(SETTINGS_SERVICE) private readonly settingsService: BaseSettingsGetService,
    private readonly templateRef: TemplateRef<any>) {
  }

  ngOnInit(): void {
    combineLatest([
      this.profileService.getCurrentProfile(),
      this.settingsService.getSettings(false)
    ])
      .pipe(
        mergeMap(([profile, settings]) => this.shouldDisplayMenuItem(profile, settings))
      )
      .subscribe(shouldDisplay => {
        this.initializeView(shouldDisplay);
      });
  }

  private initializeView(shouldDisplayElement: boolean): void {
    if (!shouldDisplayElement) {
      this.viewRef = null;
      this.viewContainer.clear();

      return;
    }

    const viewNotExist = !this.viewRef;

    if (viewNotExist) {
      this.viewContainer.clear();
      this.viewRef = this.viewContainer.createEmbeddedView(this.templateRef);

      this.viewRef.detectChanges();
    }
  }

  private shouldDisplayMenuItem(profile: ProfileUserDTO, settings: SettingsModel): Observable<boolean> {
    const requiredKeys = this.menuItemFeatureMap[this.displayMenuItem] ?? [];
    const hasNoRequiredFeatures = requiredKeys.length === 0;
    const hasEnabledFeature = Object.keys(settings.features || {}).some(key => requiredKeys.includes(key) && settings.features?.[key]);

    const enabled = hasNoRequiredFeatures || hasEnabledFeature;

    if (!enabled) {
      return of(false);
    }

    if (this.displayMenuItemAdminPlatform) {
      return of(true);
    }

    switch (this.displayMenuItem) {
      case MenuItemKey.ADMIN_PANEL:
        return of(!profile?.roles?.includes(UserRole.LEARNER));
      case MenuItemKey.MANAGER_VIEW:
        return of(settings?.dashboard?.managerView?.enableForRoles?.indexOf(profile?.roles?.[0]) !== -1);
      case MenuItemKey.HELP:
        return of(settings.legal.support?.enabled);
      case MenuItemKey.AI_CHAT:
        return of(settings.features.aiChatbot && (settings.integrations?.botsonic?.enabled || settings?.integrations?.copilot?.enabled));
      default:
        return of(true);
    }
  }
}
